import { Suspense, useEffect, useState } from "react"
import { Navigate, Route, Routes, useNavigate } from "react-router-dom"
import { Toaster } from "sonner"
import { ThemeProvider } from "@/components/theme-provider"
import { AuthProvider, useAuth } from "@/context/auth-context"
import { CampusProvider } from "@/context/campus-context"
import { AdminPage } from "@/pages/admin/AdminPage"
import { AdminSettingsPage } from "@/pages/admin/AdminSettingsPage"
import { EvolutionPanel } from "@/pages/admin/EvolutionPanel"
import HomePage from "@/pages/HomePage"
import KanbanPage from "@/pages/kanban/KanbanPage"
import LeadsTablePage from "@/pages/leads/LeadsTablePage"
import LoginPage from "@/pages/LoginPage"
import QRSharePage from "@/pages/QRSharePage"
import { AppFooter } from "@/components/app-footer"
import { CommandPalette } from "@/components/command-palette"

// Componente interno que sí tiene acceso a los hooks de router + auth
function GlobalShortcuts() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [paletteOpen, setPaletteOpen] = useState(false)

  useEffect(() => {
    if (!user) return

    // Helper: detecta si el target es un input/textarea editable
    const isTypingTarget = (target) => {
      if (!target) return false
      const tag = (target.tagName || "").toLowerCase()
      if (tag === "input" || tag === "textarea" || tag === "select") return true
      if (target.isContentEditable) return true
      return false
    }

    const handleKeyDown = (e) => {
      // ─── Atajos con Ctrl/Cmd (funcionan SIEMPRE, incluso en inputs) ───
      const isCtrl = e.ctrlKey || e.metaKey

      // Cmd/Ctrl + K → toggle búsqueda global
      if (isCtrl && (e.key === "k" || e.key === "K")) {
        e.preventDefault()
        setPaletteOpen((v) => !v)
        return
      }

      // ─── Atajos de tecla única (NO disparan dentro de inputs) ───
      // Solo si no estás escribiendo y no usas modificadores raros
      if (isCtrl || e.altKey || e.shiftKey) return
      if (isTypingTarget(e.target)) return
      // Si la búsqueda ya está abierta no queremos que N/T/B la cierren
      if (paletteOpen) return

      const k = e.key.toLowerCase()

      if (k === "n") {
        e.preventDefault()
        navigate("/kanban")
        setTimeout(() => window.dispatchEvent(new CustomEvent("openNewLead")), 100)
        return
      }
      if (k === "t") {
        e.preventDefault()
        navigate("/kanban")
        setTimeout(() => window.dispatchEvent(new CustomEvent("openTasksPanel")), 100)
        return
      }
      if (k === "b" || k === "/") {
        e.preventDefault()
        setPaletteOpen(true)
        return
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [user, navigate, paletteOpen])

  return <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <CampusProvider>
            <Toaster
              position="top-right"
              richColors
              closeButton
              toastOptions={{
                style: { fontFamily: "Inter, system-ui, sans-serif" },
                duration: 3000,
              }}
            />
            <div className="min-h-screen flex flex-col">
              <Suspense
                fallback={
                  <div className="min-h-screen flex items-center justify-center bg-background">
                    <div className="flex flex-col items-center gap-6 w-full max-w-sm px-6">
                      <div className="animate-pulse h-16 w-16 rounded-2xl bg-slate-200/80 dark:bg-slate-700/50" />
                      <div className="w-full space-y-3">
                        <div className="animate-pulse h-4 w-3/4 mx-auto rounded-md bg-slate-200/80 dark:bg-slate-700/50" />
                        <div className="animate-pulse h-3 w-1/2 mx-auto rounded-md bg-slate-200/80 dark:bg-slate-700/50" />
                      </div>
                      <div className="w-full space-y-2 pt-4">
                        <div className="animate-pulse h-10 w-full rounded-xl bg-slate-200/80 dark:bg-slate-700/50" />
                        <div className="animate-pulse h-10 w-full rounded-xl bg-slate-200/80 dark:bg-slate-700/50" />
                      </div>
                    </div>
                  </div>
                }
              >
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  {/* Ruta pública: compartir QR de instancia (sin auth) */}
                  <Route path="/qr/:instance" element={<QRSharePage />} />
                  <Route path="/" element={<HomePage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/admin/settings" element={<AdminSettingsPage />} />
                  <Route path="/admin/evolution" element={<EvolutionPanel />} />
                  <Route path="/kanban" element={<KanbanPage />} />
                  <Route path="/leads" element={<LeadsTablePage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                {/* Command Palette global + atajos Ctrl+K / Ctrl+N / Ctrl+T */}
                <GlobalShortcuts />
              </Suspense>
              <AppFooter />
            </div>
        </CampusProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}
